import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useContext,
} from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Chip,
  Divider,
  InputAdornment,
  IconButton,
  Stack,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  MenuItem,
  Badge,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import PawnNavButtons from "./pawnNavButtons";
import DocumentViewerDialog from "../TravelERP/DocumentViewerDialog";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import {
  CameraCaptureDialog,
  normalizeImageFile,
} from "../../utils/imageCapture";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ImageIcon from "@mui/icons-material/Image";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// Extracted sub-components + shared constants/helpers live under ./pawnCustomer
import {
  PRIMARY,
  BG,
  API_BASE_URL,
  validateAadhar,
  validatePAN,
  DEFAULT_PAWN_DOCUMENTS,
  initialPawnCustomerState,
  SORT_OPTIONS,
  glassChipSx,
  mobileFieldProps,
  sanitizeMobileInput,
} from "./pawnCustomer/shared";
import ImageSourceDialog from "./pawnCustomer/ImageSourceDialog";
import OTPDialog from "./pawnCustomer/OTPDialog";
import NomineesSection from "./pawnCustomer/NomineesSection";
import BulkCustomerDialog from "./pawnCustomer/BulkCustomerDialog";
import GroupAddRoundedIcon from "@mui/icons-material/GroupAddRounded";

// Every input on the form runs a notch smaller than MUI's `small` default —
// same control heights, less type and less helper-text leading — so the whole
// form fits the page without a scroll. Spread it, don't replace it: a field that
// needs its own `sx` keys spreads this first.
const compactFieldSx = {
  "& .MuiInputBase-input": { fontSize: "0.8rem" },
  "& .MuiInputLabel-root": { fontSize: "0.8rem" },
  "& .MuiFormHelperText-root": {
    fontSize: "0.62rem",
    lineHeight: 1.3,
    mt: 0.25,
    mx: 0.75,
  },
  "& .MuiInputAdornment-root .MuiTypography-root": { fontSize: "0.8rem" },
};

// ─── Main Component ───────────────────────────────────────────────────────────
const PawnCustomerMaster = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { currentStoreInfo, userInfo } = useContext(AutoTaskAuthContext);

  const [userID, setUserID] = useState(null);
  const [userStoreID, setUserStoreID] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);

  const [searchText, setSearchText] = useState("");
  const [searchMode, setSearchMode] = useState("wild");
  const [sortOrder, setSortOrder] = useState("ASC");

  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [customerDetail, setCustomerDetail] = useState({
    ...initialPawnCustomerState,
    documents: JSON.parse(JSON.stringify(DEFAULT_PAWN_DOCUMENTS)),
  });
  const [isNewCustomer, setIsNewCustomer] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isListLoading, setIsListLoading] = useState(false);

  // Nominees state
  // create mode: held locally until customer is saved
  const [localNominees, setLocalNominees] = useState([]);
  // edit mode: fetched from server
  const [serverNominees, setServerNominees] = useState([]);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  // While the delete dialog runs its active-loan pre-check.
  const [isCheckingLoans, setIsCheckingLoans] = useState(false);
  // Non-null ⇒ deletion is blocked; holds the message to display.
  const [deleteBlockInfo, setDeleteBlockInfo] = useState(null);
  const [viewerDoc, setViewerDoc] = useState(null);

  const [duplicateMobile, setDuplicateMobile] = useState(null);
  const [isMobileChecking, setIsMobileChecking] = useState(false);
  const mobileCheckTimeoutRef = useRef(null);

  const [partyCodeDuplicate, setPartyCodeDuplicate] = useState(null);
  const partyCodeTimeoutRef = useRef(null);

  const [fieldErrors, setFieldErrors] = useState({
    name: false,
    mobile: false,
    pickupAddress: false,
  });

  const [openOTPDialog, setOpenOTPDialog] = useState(false);
  const [pendingCustomerData, setPendingCustomerData] = useState(null);

  // Excel-driven bulk import (used to migrate the old pawn app's customers).
  const [openBulkDialog, setOpenBulkDialog] = useState(false);

  // Store setting: when true, customer creation requires OTP verification;
  // when false, the customer is created directly. Loaded from pawn settings.
  const [otpForCust, setOtpForCust] = useState(false);

  // Store setting: when true two customers may share a mobile number, and the
  // duplicate warning asks for confirmation instead of refusing the save.
  const [allowDuplicateMobile, setAllowDuplicateMobile] = useState(false);
  // Non-null ⇒ the save is paused on "is this really a different person?",
  // holding the payload to create once that is confirmed.
  const [duplicateConfirm, setDuplicateConfirm] = useState(null);

  const [imageSourceDialog, setImageSourceDialog] = useState({
    open: false,
    index: null,
  });
  // Document index currently capturing from the live camera.
  const [customerCameraIndex, setCustomerCameraIndex] = useState(null);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalCustomers, setTotalCustomers] = useState(0);

  const searchTimeoutRef = useRef(null);
  const listRef = useRef(null);

  const [snackBarConfig, setSnackBarConfig] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const closeSnackbar = () =>
    setSnackBarConfig((prev) => ({ ...prev, open: false }));

  const showSnack = useCallback((message, severity = "info") => {
    setSnackBarConfig({ open: true, message, severity });
  }, []);

  useEffect(() => {
    return () => {
      if (mobileCheckTimeoutRef.current)
        clearTimeout(mobileCheckTimeoutRef.current);
      if (partyCodeTimeoutRef.current)
        clearTimeout(partyCodeTimeoutRef.current);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const updateNomineeInState = useCallback(
    (updatedNominee) => {
      if (!isNewCustomer) {
        // Edit mode – update serverNominees
        setServerNominees((prev) =>
          prev.map((nom) =>
            nom.link_id === updatedNominee.link_id
              ? { ...nom, ...updatedNominee }
              : nom,
          ),
        );
      } else {
        // Create mode – update localNominees
        setLocalNominees((prev) =>
          prev.map((nom, idx) =>
            nom._snapshot?.link_id === updatedNominee.link_id
              ? { ...nom, ...updatedNominee }
              : nom,
          ),
        );
      }
    },
    [isNewCustomer],
  );

  // Initialize user/store from context or location state
  useEffect(() => {
    const initializeData = () => {
      try {
        if (
          location.state?.user_id !== undefined &&
          location.state?.store_id !== undefined
        ) {
          setUserID(location.state.user_id);
          setUserStoreID(location.state.store_id);
          setIsInitializing(false);
          return;
        }
        if (userInfo?.id !== undefined && currentStoreInfo?.id !== undefined) {
          setUserID(userInfo.id);
          setUserStoreID(currentStoreInfo.id);
          setIsInitializing(false);
        } else {
          setIsInitializing(false);
        }
      } catch (error) {
        console.error("❌ Error initializing data:", error);
        setIsInitializing(false);
      }
    };
    initializeData();
  }, [location.state, userInfo, currentStoreInfo]);

  // Load the store's "OTP validation for customer creation" pawn setting.
  useEffect(() => {
    if (userStoreID === undefined || userStoreID === null) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/pawn/settings/${userStoreID}`,
        );
        if (!cancelled && res.data?.success && res.data.data) {
          setOtpForCust(Boolean(res.data.data.OTP_for_cust));
          setAllowDuplicateMobile(
            Boolean(res.data.data.allow_duplicate_mobile),
          );
        }
      } catch (error) {
        console.error("Error loading pawn settings:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userStoreID]);

  const handleChange = (field, value) => {
    setCustomerDetail((p) => ({ ...p, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleAnnualIncomeChange = (value) => {
    if (value === "" || /^\d+(\.\d*)?$/.test(value)) {
      handleChange("annualIncome", value);
    }
  };

  const handleRemoveOtherDocument = () => {
    const filteredDocs = customerDetail.documents.filter(
      (doc) => doc.name !== "Other",
    );
    handleChange("documents", filteredDocs);
    showSnack("Other document removed. Save to apply changes.", "info");
  };

  const handleAddOtherDocument = () => {
    if (customerDetail.documents.some((doc) => doc.name === "Other")) {
      showSnack("Other document already exists", "warning");
      return;
    }
    const newOtherDoc = {
      name: "Other",
      number: "",
      file: null,
      fileName: "",
      renewalDate: "",
    };
    handleChange("documents", [...customerDetail.documents, newOtherDoc]);
    showSnack("Other document added. Save to apply changes.", "info");
  };

  const resetForm = () => {
    setCustomerDetail({
      ...initialPawnCustomerState,
      documents: JSON.parse(JSON.stringify(DEFAULT_PAWN_DOCUMENTS)),
    });
    setSelectedCustomerId(null);
    setIsNewCustomer(true);
    setDuplicateMobile(null);
    setPartyCodeDuplicate(null);
    setFieldErrors({ name: false, mobile: false, pickupAddress: false });
    setPendingCustomerData(null);
    setOpenOTPDialog(false);
    setLocalNominees([]);
    setServerNominees([]);
  };

  const handleAddNewCustomer = () => {
    resetForm();
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
  };

  const handleCancel = () => {
    resetForm();
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
  };

  const checkPartyCodeDuplicate = useCallback(
    async (code) => {
      if (!code?.trim() || userStoreID === null || userStoreID === undefined) {
        setPartyCodeDuplicate(null);
        return;
      }
      partyCodeTimeoutRef.current = setTimeout(async () => {
        try {
          const url = `${API_BASE_URL}/api/pawn/customers/store/${userStoreID}/party/${code.trim()}`;
          const res = await axios.get(url);
          if (res.data.success && res.data.data) {
            setPartyCodeDuplicate(res.data.data);
          } else {
            setPartyCodeDuplicate(null);
          }
        } catch (error) {
          setPartyCodeDuplicate(null);
        }
      }, 500);
    },
    [userStoreID],
  );

  const checkMobileDuplicate = useCallback(
    async (number) => {
      const clean = number?.toString().replace(/\D/g, "") || "";
      if (clean.length < 3) {
        setDuplicateMobile(null);
        return;
      }
      if (userStoreID === null || userStoreID === undefined) return;

      mobileCheckTimeoutRef.current = setTimeout(async () => {
        try {
          setIsMobileChecking(true);
          const params = { numbers: clean };
          if (selectedCustomerId) {
            params.exclude_customer_id = selectedCustomerId;
          }
          const url = `${API_BASE_URL}/api/pawn/customers/check-duplicate/${userStoreID}`;
          const res = await axios.get(url, { params });
          if (res.data.success) {
            const result = res.data.data;
            setDuplicateMobile(
              result.isDuplicate ? result.existingCustomer : null,
            );
          }
        } catch (error) {
          console.error("🔥 API Error:", error);
        } finally {
          setIsMobileChecking(false);
        }
      }, 600);
    },
    [userStoreID, selectedCustomerId],
  );

  const updateDocument = (i, field, value) => {
    let processedValue = value;

    const doc = customerDetail.documents[i];
    if (doc.name === "Aadhar") {
      const validation = validateAadhar(value);
      processedValue = validation.value;
      if (validation.error) showSnack(validation.error, "warning");
    } else if (doc.name === "PAN") {
      const validation = validatePAN(value);
      processedValue = validation.value;
      if (validation.error) showSnack(validation.error, "warning");
    }

    setCustomerDetail((prev) => {
      const docs = [...prev.documents];
      docs[i] = { ...docs[i], [field]: processedValue };
      return { ...prev, documents: docs };
    });
  };

  const setCustomerDocFile = (i, dataUrl, fileName) => {
    setCustomerDetail((prev) => {
      const docs = [...prev.documents];
      docs[i] = { ...docs[i], file: dataUrl, fileName };
      return { ...prev, documents: docs };
    });
  };

  const handleFileUpload = async (i, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Bake in EXIF orientation so phone photos don't come out rotated.
    const dataUrl = await normalizeImageFile(file);
    setCustomerDocFile(i, dataUrl, file.name);
    e.target.value = "";
  };

  const customersFetcher = useCallback(
    async (pageNum = 1, isLoadMore = false, silent = false) => {
      if (!userStoreID && userStoreID !== 0) return;
      try {
        if (!isLoadMore && !silent) setIsListLoading(true);
        else if (isLoadMore) setIsLoadingMore(true);

        const res = await axios.get(
          `${API_BASE_URL}/api/pawn/customers/store/${userStoreID}`,
          {
            params: {
              page: pageNum,
              limit: 25,
              search: searchText.trim(),
              search_mode: searchMode,
              sort: sortOrder,
            },
          },
        );

        const data = res.data?.data ?? [];
        const pagination = res.data?.pagination;
        if (!res.data.success) throw res.data;

        if (isLoadMore) setCustomers((prev) => [...prev, ...data]);
        else setCustomers(data);

        setTotalCustomers(pagination?.total || 0);
        setHasMore(pagination?.hasNextPage || false);
        setPage(pageNum);
      } catch (error) {
        console.error("Error fetching customers:", error);
        showSnack(error.statusMessage ?? "Failed to load customers", "error");
      } finally {
        setIsListLoading(false);
        setIsLoadingMore(false);
      }
    },
    [userStoreID, searchText, searchMode, sortOrder, showSnack],
  );

  useEffect(() => {
    if (userStoreID !== null && userStoreID !== undefined) {
      setCustomers([]);
      setPage(1);
      setHasMore(true);
    }
  }, [userStoreID]);

  useEffect(() => {
    if (userStoreID === null || userStoreID === undefined) return;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      setCustomers([]);
      setHasMore(true);
      customersFetcher(1, false);
    }, 500);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchText, sortOrder, searchMode, userStoreID, customersFetcher]);

  const handleScroll = useCallback(() => {
    if (!listRef.current || isLoadingMore || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      customersFetcher(page + 1, true);
    }
  }, [page, hasMore, isLoadingMore, customersFetcher]);

  // ── Fetch One Customer ──────────────────────────────────────────────────────
  const fetchOneCustomer = async (customerID) => {
    try {
      setIsListLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/customers/store/${userStoreID}/id/${customerID}`,
      );

      if (!res.data.success) throw res.data;

      const fc = res.data.data;

      const serverDocs = fc.customer_documents || [];
      const docSlots = DEFAULT_PAWN_DOCUMENTS.map((slot) => {
        const found = serverDocs.find(
          (d) => d.name?.toLowerCase() === slot.name.toLowerCase(),
        );
        if (found) {
          return {
            doc_id: found.doc_id,
            name: slot.name,
            number: found.number || "",
            file: found.file || null,
            fileName: found.fileName || "",
            renewalDate: found.renewalDate || "",
          };
        }
        if (slot.name === "Other") return null;
        return { ...slot };
      }).filter(Boolean);

      const otherDocs = serverDocs.filter(
        (d) => d.name?.toLowerCase() === "other",
      );
      otherDocs.forEach((d) => {
        if (!docSlots.find((s) => s.name === "Other")) {
          docSlots.push({
            doc_id: d.doc_id,
            name: "Other",
            number: d.number || "",
            file: d.file || null,
            fileName: d.fileName || "",
            renewalDate: d.renewalDate || "",
          });
        }
      });

      setCustomerDetail({
        partyCode: fc.party_code || "",
        mobile: fc.mobile?.toString() || "",
        name: fc.name || "",
        dob: fc.dob || "",
        pickupAddress: fc.pickupAddress || "",
        relationType:
          fc.relative_relation === "None" ? "" : fc.relative_relation || "",
        relationName: fc.relative_name || "",
        annualIncome: fc.annual_income?.toString() || "",
        note: fc.note || "",
        documents: docSlots,
      });

      // Populate server nominees
      const nomineesFromServer = (fc.nominees || []).map((n) => ({
        link_id: n.link_id,
        nominee_id: n.nominee_id,
        name: n.name,
        mobile: n.mobile,
        dob: n.dob,
        pickup_address: n.pickup_address,
        relative_relation: n.relative_relation,
        relative_name: n.relative_name,
        annual_income: n.annual_income,
        note: n.note,
        party_code: n.party_code,
        relation: n.relation,
        share_percent: n.share_percent,
        is_primary: n.is_primary,
        cust_role: n.cust_role,
        documents: (n.documents || []).map((d) => ({
          doc_id: d.doc_id,
          name: d.name,
          number: d.number || "",
          file: d.file || null,
          fileName: d.fileName || "",
          renewalDate: d.renewalDate || "",
        })),
      }));
      setServerNominees(nomineesFromServer);
      setLocalNominees([]);

      setDuplicateMobile(null);
      setPartyCodeDuplicate(null);
      setSelectedCustomerId(customerID);
      setIsNewCustomer(false);
      setPendingCustomerData(null);
      setOpenOTPDialog(false);
      setFieldErrors({ name: false, mobile: false, pickupAddress: false });

      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);

      showSnack("Customer loaded successfully", "success");
    } catch (error) {
      console.error("Error fetching customer:", error);
      showSnack(
        error?.statusMessage || "Failed to load customer data",
        "error",
      );
    } finally {
      setIsListLoading(false);
    }
  };

  // Re-fetch nominees only (called after nominee save/remove in edit mode)
  const refreshNominees = useCallback(async () => {
    if (!selectedCustomerId || !userStoreID) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/customers/store/${userStoreID}/id/${selectedCustomerId}`,
      );
      if (!res.data.success) return;
      const fc = res.data.data;
      const nomineesFromServer = (fc.nominees || []).map((n) => ({
        link_id: n.link_id,
        nominee_id: n.nominee_id,
        name: n.name,
        mobile: n.mobile,
        dob: n.dob,
        pickup_address: n.pickup_address,
        relative_relation: n.relative_relation,
        relative_name: n.relative_name,
        annual_income: n.annual_income,
        note: n.note,
        party_code: n.party_code,
        relation: n.relation,
        share_percent: n.share_percent,
        is_primary: n.is_primary,
        cust_role: n.cust_role,
        documents: (n.documents || []).map((d) => ({
          doc_id: d.doc_id,
          name: d.name,
          number: d.number || "",
          file: d.file || null,
          fileName: d.fileName || "",
          renewalDate: d.renewalDate || "",
        })),
      }));
      setServerNominees(nomineesFromServer);
    } catch (error) {
      console.error("Error refreshing nominees:", error);
    }
  }, [selectedCustomerId, userStoreID]);

  // Opens the delete dialog, but first checks for active loans. If any exist the
  // dialog shows a blocking warning instead of the confirm-delete action.
  const handleDeleteClick = async (customer) => {
    setSelectedCustomer(customer);
    setDeleteBlockInfo(null);
    setIsCheckingLoans(true);
    setOpenDialog(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/customers/${customer.customer_id}/active-loans`,
        { params: { store_id: userStoreID } },
      );
      if (res.data.success && res.data.data?.blocked) {
        setDeleteBlockInfo({
          message:
            res.data.data.message ||
            "This customer has existing unsettled loans. Cannot proceed for deletion.",
        });
      }
    } catch (error) {
      // If the pre-check fails, fall through to the confirm dialog — the delete
      // endpoint itself still guards against active loans server-side.
      console.error("Error checking active loans:", error);
    } finally {
      setIsCheckingLoans(false);
    }
  };

  const deleteCustomer = async (customerID) => {
    try {
      setIsSaving(true);
      const res = await axios.delete(
        `${API_BASE_URL}/api/pawn/customers/${customerID}`,
        { data: { store_id: userStoreID } },
      );
      if (!res.data.success) throw res.data;

      setSelectedCustomer(null);
      setOpenDialog(false);
      handleCancel();
      showSnack(
        res.data.statusMessage || "Customer deleted successfully!",
        "success",
      );
      setPage(1);
      setCustomers([]);
      setHasMore(true);
      customersFetcher(1, false, true);
    } catch (error) {
      console.error("Error deleting customer:", error);
      // Close the confirm dialog so the (often long) guard message is visible.
      setOpenDialog(false);
      showSnack(error?.statusMessage || "Failed to delete customer", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const sendOTPAndSave = async (payload) => {
    // Pawn customer-master OTP — sends an OTP to the customer's mobile without
    // requiring that number to already exist in the Users table.
    const url = `${API_BASE_URL}/api/pawn/customers/send-otp`;
    try {
      const res = await axios.post(url, {
        mobile: payload.mobile,
        store_id: payload.store_id,
      });
      const { success, statusCode, statusMessage } = res.data;
      if (!success || statusCode !== 1) {
        showSnack(statusMessage || "Failed to send OTP", "error");
        return false;
      }
      showSnack("OTP sent successfully", "success");
      return true;
    } catch (error) {
      showSnack("Failed to send OTP. Please try again.", "error");
      return false;
    }
  };

  const saveCustomerAfterOTP = async (customerData = pendingCustomerData) => {
    if (!customerData) return;
    try {
      setIsSaving(true);
      const apiResponse = await axios.post(
        `${API_BASE_URL}/api/pawn/customers`,
        customerData,
      );
      if (!apiResponse.data.success) throw apiResponse.data;

      showSnack(
        apiResponse.data.statusMessage || "Customer saved successfully",
        "success",
      );
      setPage(1);
      setCustomers([]);
      setHasMore(true);
      customersFetcher(1, false, true);

      setTimeout(() => {
        resetForm();
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 300);
    } catch (error) {
      console.error("Error saving customer:", error);
      showSnack(error?.statusMessage || "Operation failed", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const buildDocumentsPayload = () =>
    customerDetail.documents
      .filter((doc) => {
        if (doc.name === "Other") {
          const hasFile = doc.file && doc.file !== "No file";
          const hasNumber = doc.number?.trim();
          return hasFile || hasNumber;
        }
        return true;
      })
      .map((doc) => ({
        doc_id: doc.doc_id,
        name: doc.name,
        number: doc.number,
        file: doc.file,
        fileName: doc.fileName,
        renewalDate: doc.renewalDate || null,
      }));

  // Build nominees array for create payload
  const buildNomineesPayload = () =>
    localNominees.map((n) => ({
      name: n.name,
      mobile: n.mobile,
      dob: n.dob || null,
      pickup_address: n.pickup_address || null,
      relative_relation: n.relative_relation || "None",
      relative_name: n.relative_name || null,
      annual_income: n.annual_income || null,
      note: n.note || null,
      party_code: n.party_code || null,
      relation: n.relation || null,
      share_percent: n.share_percent || null,
      is_primary: n.is_primary || 0,
      documents: n.documents || [],
    }));

  const handleSave = async () => {
    const errors = {
      name: !customerDetail.name.trim(),
      mobile: !customerDetail.mobile.trim(),
      pickupAddress: !customerDetail.pickupAddress.trim(),
    };
    setFieldErrors(errors);

    if (Object.values(errors).some(Boolean)) {
      showSnack(
        "Please fill in all required fields: Name, Mobile, and Address.",
        "error",
      );
      return;
    }

    if (selectedCustomerId && !isNewCustomer) {
      await saveCustomerDirectly();
      return;
    }

    const payload = {
      party_code: customerDetail.partyCode || null,
      store_id: userStoreID,
      created_by: userID,
      name: customerDetail.name,
      mobile: customerDetail.mobile,
      dob: customerDetail.dob || null,
      pickup_address: customerDetail.pickupAddress,
      relative_relation: customerDetail.relationType || "None",
      relative_name: customerDetail.relationName || null,
      annual_income: customerDetail.annualIncome
        ? parseFloat(customerDetail.annualIncome)
        : null,
      note: customerDetail.note || null,
      documents: buildDocumentsPayload(),
      nominees: buildNomineesPayload(),
    };

    if (duplicateMobile) {
      // A mobile number is a contact number, not an identity: families share
      // one phone. Stores that have said so get asked to confirm; the rest keep
      // the hard guard, which is what catches the same customer typed twice.
      if (!allowDuplicateMobile) {
        showSnack(
          `Mobile number already belongs to ${duplicateMobile.name}. Please use a different number.`,
          "error",
        );
        return;
      }
      setDuplicateConfirm(payload);
      return;
    }

    await proceedWithCreate(payload);
  };

  // Everything after the duplicate question: OTP if the store wants it,
  // otherwise straight to the create call.
  const proceedWithCreate = async (payload) => {
    setPendingCustomerData(payload);

    // When OTP validation is disabled for this store, create the customer
    // directly without the OTP send/verify step.
    if (!otpForCust) {
      await saveCustomerAfterOTP(payload);
      return;
    }

    const otpSent = await sendOTPAndSave(payload);
    if (otpSent) {
      setOpenOTPDialog(true);
    } else {
      setPendingCustomerData(null);
    }
  };

  const saveCustomerDirectly = async () => {
    const payload = {
      party_code: customerDetail.partyCode || null,
      store_id: userStoreID,
      created_by: userID,
      name: customerDetail.name,
      mobile: customerDetail.mobile,
      dob: customerDetail.dob || null,
      pickup_address: customerDetail.pickupAddress,
      relative_relation: customerDetail.relationType || "None",
      relative_name: customerDetail.relationName || null,
      annual_income: customerDetail.annualIncome
        ? parseFloat(customerDetail.annualIncome)
        : null,
      note: customerDetail.note || null,
      documents: buildDocumentsPayload(),
    };

    try {
      setIsSaving(true);
      const apiResponse = await axios.put(
        `${API_BASE_URL}/api/pawn/customers/${selectedCustomerId}`,
        payload,
      );
      if (!apiResponse.data.success) throw apiResponse.data;

      showSnack(
        apiResponse.data.statusMessage || "Customer updated successfully",
        "success",
      );
      setPage(1);
      setCustomers([]);
      setHasMore(true);
      customersFetcher(1, false, true);

      setTimeout(() => {
        resetForm();
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 300);
    } catch (error) {
      console.error("Error updating customer:", error);
      showSnack(error?.statusMessage || "Operation failed", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const hasOtherDocument = customerDetail.documents.some(
    (doc) => doc.name === "Other",
  );

  if (isInitializing) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (userID === null || userStoreID === null) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate("/login")}
            >
              Go to Login
            </Button>
          }
        >
          Unable to load user or store information. Please log in again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: BG,
        // Same rule as the report screens: the PAGE does not scroll. The screen
        // is a flex column — header (fixed) + the two panels, which split the
        // leftover height and each scroll inside themselves. Vertical stays
        // `auto` rather than `hidden` so a very short window degrades to a page
        // scroll instead of clipping the form away.
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        overflowY: "auto",
        overflowX: "hidden",
        p: { xs: 1, sm: 1.25, md: 1.5 },
      }}
    >
      {isSaving && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            zIndex: 9999,
            background: `linear-gradient(90deg, ${PRIMARY}, #3B82F6)`,
            animation: "loadingBar 1.2s ease-in-out infinite",
            "@keyframes loadingBar": {
              "0%": {
                opacity: 0.6,
                transform: "scaleX(0.3)",
                transformOrigin: "left",
              },
              "50%": {
                opacity: 1,
                transform: "scaleX(0.8)",
                transformOrigin: "left",
              },
              "100%": {
                opacity: 0.6,
                transform: "scaleX(1)",
                transformOrigin: "left",
              },
            },
          }}
        />
      )}

      {/* Mobile header */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          alignItems: "center",
          gap: 1.25,
          mb: 1,
          pb: 0.75,
          flexShrink: 0,
          borderBottom: `1px solid ${PRIMARY}20`,
        }}
      >
        <PawnNavButtons />
        <PersonRoundedIcon sx={{ fill: PRIMARY, fontSize: 20 }} />
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ color: PRIMARY }}
        >
          Pawn Customer Master
        </Typography>
      </Box>

      {/* ═════════ The two panels, splitting whatever height is left ═════════ */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 1, md: 1.5 },
        }}
      >
        {/* ═══════════════════════ LEFT: CUSTOMER LIST ═══════════════════════ */}
        <Box
          sx={{
            order: { xs: 2, md: 1 },
            flex: "0 0 auto",
            width: { xs: "100%", md: 300, lg: 330 },
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            // Phones stack the panels, so the list gets a fixed slice of the
            // screen rather than "whatever is left" (which would be nothing).
            // That internal scroll is also what pages in the next 25 rows.
            height: { xs: 380, md: "auto" },
          }}
        >
          <Paper
            elevation={3}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Gradient header — title + count, search, scope + sort chips */}
            <Box
              sx={{
                flexShrink: 0,
                background:
                  "linear-gradient(90deg, #1D4ED8 0%, #2563EB 50%, #3B82F6 100%)",
                p: 1.25,
              }}
            >
              <Box
                sx={{
                  display: { xs: "none", md: "flex" },
                  alignItems: "center",
                  gap: 1,
                  mb: 0.75,
                }}
              >
                {/* Same pair as every other pawn screen — translucent chips,
                    since this header is the blue gradient. */}
                <PawnNavButtons onDark gap={1} />
                <PersonRoundedIcon sx={{ fill: "#FFF", fontSize: 18 }} />
                <Typography
                  fontWeight={700}
                  sx={{
                    color: "#FFF",
                    fontSize: "0.82rem",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Customers
                </Typography>
                {/* Row count rides in the header instead of its own strip. */}
                <Box
                  sx={{
                    ml: "auto",
                    flexShrink: 0,
                    px: 0.85,
                    py: 0.15,
                    borderRadius: "10px",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "#FFF",
                    backgroundColor: "rgba(255,255,255,0.2)",
                    border: "1px solid rgba(255,255,255,0.3)",
                  }}
                >
                  {isListLoading ? "…" : totalCustomers}
                </Box>
              </Box>

              {/* Search */}
              <TextField
                fullWidth
                size="small"
                placeholder="Name, code, mobile…"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{ color: "rgba(255,255,255,0.7)", fontSize: 18 }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 0.75,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    color: "#fff",
                    fontSize: "0.8rem",
                    background: "rgba(255, 255, 255, 0.15)",
                    backdropFilter: "blur(12px)",
                    border: "1px solid rgba(255,255,255,0.25)",
                    "& fieldset": { border: "none" },
                    "& input": { py: 0.75 },
                    "&:hover": { background: "rgba(255, 255, 255, 0.22)" },
                    "&.Mui-focused": {
                      background: "rgba(255, 255, 255, 0.25)",
                      boxShadow: "0 0 0 2px rgba(255,255,255,0.25)",
                    },
                  },
                }}
              />

              {/* Search scope + sort: two chip rows, no labelled blocks */}
              <Box sx={{ display: "flex", gap: 0.5, mb: 0.5 }}>
                {[
                  { label: "Start", value: "start" },
                  { label: "Wild", value: "wild" },
                  { label: "End", value: "end" },
                ].map((mode) => (
                  <Chip
                    key={mode.value}
                    label={mode.label}
                    size="small"
                    clickable
                    onClick={() => setSearchMode(mode.value)}
                    sx={{
                      ...glassChipSx(searchMode === mode.value),
                      height: 20,
                      fontSize: "0.63rem",
                      "& .MuiChip-label": { px: 0.85 },
                    }}
                  />
                ))}
              </Box>
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {SORT_OPTIONS.map((opt) => (
                  <Chip
                    key={opt.value}
                    label={opt.short || opt.label}
                    size="small"
                    clickable
                    onClick={() => setSortOrder(opt.value)}
                    sx={{
                      ...glassChipSx(sortOrder === opt.value),
                      height: 20,
                      fontSize: "0.63rem",
                      "& .MuiChip-label": { px: 0.85 },
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* New Customer + Bulk Create, side by side */}
            <Stack
              direction="row"
              spacing={0.75}
              sx={{ px: 1, py: 0.75, flexShrink: 0 }}
            >
              <Button
                variant="contained"
                startIcon={<PersonAddRoundedIcon sx={{ fontSize: 16 }} />}
                onClick={handleAddNewCustomer}
                sx={{
                  flex: 1,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.72rem",
                  lineHeight: 1.2,
                  py: 0.5,
                  minHeight: 32,
                  borderRadius: "10px",
                  backgroundColor: PRIMARY,
                  boxShadow: "0px 3px 10px rgba(0,0,0,0.15)",
                  "&:hover": { backgroundColor: PRIMARY },
                }}
              >
                New
              </Button>

              <Tooltip title="Create many customers at once from an Excel sheet">
                <Button
                  variant="outlined"
                  startIcon={<GroupAddRoundedIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setOpenBulkDialog(true)}
                  sx={{
                    flex: 1.6,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    lineHeight: 1.2,
                    py: 0.5,
                    minHeight: 32,
                    borderRadius: "10px",
                    color: PRIMARY,
                    borderColor: PRIMARY,
                    backgroundColor: "#fff",
                    "&:hover": {
                      borderColor: PRIMARY,
                      backgroundColor: `${PRIMARY}0D`,
                    },
                  }}
                >
                  Bulk Create
                </Button>
              </Tooltip>
            </Stack>

            <Box sx={{ height: "1px", bgcolor: "#E2E8F0", flexShrink: 0 }} />

            {/* Customer list — the one thing on this screen that scrolls, and
                it does it without ever showing a scrollbar. */}
            <Box
              ref={listRef}
              onScroll={handleScroll}
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                "&::-webkit-scrollbar": { width: 0, height: 0 },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                px: 0.5,
                py: 0.25,
              }}
            >
              {customers.map((customer) => {
                const isSelected = selectedCustomerId === customer.customer_id;
                return (
                  <Box
                    key={customer.customer_id}
                    onClick={() => fetchOneCustomer(customer.customer_id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      px: 0.75,
                      py: 0.5,
                      borderRadius: 1.5,
                      cursor: "pointer",
                      borderLeft: `3px solid ${
                        isSelected ? PRIMARY : "transparent"
                      }`,
                      borderBottom: "1px solid #F1F5F9",
                      backgroundColor: isSelected
                        ? `${PRIMARY}12`
                        : "transparent",
                      "&:hover": {
                        backgroundColor: isSelected
                          ? `${PRIMARY}18`
                          : `${PRIMARY}08`,
                      },
                      "&:last-of-type": { borderBottom: "none" },
                    }}
                  >
                    <Badge
                      badgeContent={customer.nominee_count || 0}
                      invisible={!customer.nominee_count}
                      sx={{
                        flexShrink: 0,
                        "& .MuiBadge-badge": {
                          backgroundColor: "#f9a825",
                          color: "#fff",
                          fontSize: "0.55rem",
                          minWidth: 14,
                          height: 14,
                          padding: 0,
                          right: 2,
                          top: 2,
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          backgroundColor: PRIMARY,
                          width: 26,
                          height: 26,
                          fontSize: "0.7rem",
                          fontWeight: 700,
                        }}
                      >
                        {customer.name?.[0] || "?"}
                      </Avatar>
                    </Badge>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <Typography
                          title={customer.name}
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: "0.76rem",
                            fontWeight: 600,
                            lineHeight: 1.3,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {customer.name}
                        </Typography>
                        <Typography
                          sx={{
                            flexShrink: 0,
                            fontSize: "0.7rem",
                            color: "text.secondary",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {customer.mobile}
                        </Typography>
                      </Box>
                      {(customer.party_code || customer.address) && (
                        <Typography
                          title={customer.address || ""}
                          sx={{
                            display: "block",
                            fontSize: "0.65rem",
                            lineHeight: 1.35,
                            color: "text.secondary",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {customer.party_code && (
                            <Box
                              component="span"
                              sx={{ color: PRIMARY, fontWeight: 700 }}
                            >
                              {customer.party_code}
                              {customer.address ? " · " : ""}
                            </Box>
                          )}
                          {customer.address}
                        </Typography>
                      )}
                    </Box>

                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(customer);
                      }}
                      sx={{
                        flexShrink: 0,
                        p: 0.25,
                        color: "#94A3B8",
                        "&:hover": { color: "#DC2626" },
                      }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                );
              })}

              {isLoadingMore && (
                <Box
                  sx={{ display: "flex", justifyContent: "center", py: 1.5 }}
                >
                  <CircularProgress size={18} />
                </Box>
              )}
              {!hasMore && customers.length > 0 && (
                <Typography
                  align="center"
                  sx={{
                    display: "block",
                    py: 1.25,
                    fontSize: "0.65rem",
                    color: "text.secondary",
                  }}
                >
                  No more customers
                </Typography>
              )}
              {!isListLoading && customers.length === 0 && (
                <Typography
                  align="center"
                  sx={{
                    display: "block",
                    py: 3,
                    fontSize: "0.78rem",
                    color: "text.secondary",
                  }}
                >
                  No customers found
                </Typography>
              )}
            </Box>
          </Paper>
        </Box>

        {/* ═════════════════ RIGHT: THE FORM (create / edit) ═════════════════ */}
        <Box
          sx={{
            order: { xs: 1, md: 2 },
            flex: { xs: "0 0 auto", md: 1 },
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            // From md up the form owns its own scroll (nominee cards can
            // outgrow the viewport) so the page itself still never moves.
            overflowY: { xs: "visible", md: "auto" },
            overflowX: "hidden",
            "&::-webkit-scrollbar": { width: 0, height: 0 },
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <Stack
            spacing={1}
            sx={{ flex: { xs: "0 0 auto", md: 1 }, minHeight: 0 }}
          >
            {/* Customer details + documents — one card, not two */}
            <Paper
              elevation={3}
              sx={{
                p: { xs: 1.25, md: 1.5 },
                borderRadius: 2,
                flexShrink: 0,
              }}
            >
              {/* Heading row: title, KYC chips, and both actions */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mb: 1.25,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  fontWeight={700}
                  sx={{
                    color: PRIMARY,
                    fontSize: { xs: "0.85rem", md: "0.95rem" },
                  }}
                >
                  Customer Details
                  <Box
                    component="span"
                    sx={{ fontWeight: 400, color: "#C5C5C5", fontSize: 11 }}
                  >
                    {" "}
                    *required
                  </Box>
                </Typography>

                {/* KYC completeness chips */}
                {["Aadhar", "PAN", "Photo"].map((docName) => {
                  const doc = customerDetail.documents?.find(
                    (d) => d.name === docName,
                  );
                  const done = doc?.file && doc.file !== "No file";
                  return (
                    <Chip
                      key={docName}
                      label={done ? `${docName} ✓` : docName}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: "0.6rem",
                        fontWeight: 600,
                        "& .MuiChip-label": { px: 0.7 },
                        backgroundColor: done ? "#e8f5e9" : "#f5f5f5",
                        color: done ? "#2e7d32" : "#9e9e9e",
                        border: done
                          ? "1px solid #a5d6a7"
                          : "1px solid #e0e0e0",
                      }}
                    />
                  );
                })}

                <Box sx={{ flex: 1 }} />

                {/* Cancel sits with Save instead of trailing the whole form. */}
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon sx={{ fontSize: 16 }} />}
                  onClick={handleCancel}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.72rem",
                    fontWeight: 600,
                    py: 0.4,
                    minHeight: 30,
                    borderRadius: "10px",
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon sx={{ fontSize: 16 }} />}
                  onClick={handleSave}
                  sx={{
                    backgroundColor: PRIMARY,
                    textTransform: "none",
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    py: 0.4,
                    minHeight: 30,
                    borderRadius: "10px",
                    "&:hover": { backgroundColor: PRIMARY },
                  }}
                >
                  {selectedCustomerId && !isNewCustomer ? "Update" : "Save"}
                </Button>
              </Box>

              <Stack spacing={1.25}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
                  <Box sx={{ flex: "1 1 110px", minWidth: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Party Code"
                      sx={compactFieldSx}
                      value={customerDetail.partyCode || ""}
                      onChange={(e) => {
                        handleChange("partyCode", e.target.value);
                        checkPartyCodeDuplicate(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (
                          e.key === "Tab" &&
                          partyCodeDuplicate &&
                          partyCodeDuplicate.customer_id !== selectedCustomerId
                        ) {
                          e.preventDefault();
                          fetchOneCustomer(partyCodeDuplicate.customer_id);
                        }
                      }}
                      error={
                        !!partyCodeDuplicate &&
                        partyCodeDuplicate.customer_id !== selectedCustomerId
                      }
                      helperText={
                        !!partyCodeDuplicate &&
                        partyCodeDuplicate.customer_id !== selectedCustomerId
                          ? `⚠️ Belongs to: ${partyCodeDuplicate.name}`
                          : ""
                      }
                    />
                  </Box>

                  <Box sx={{ flex: "2 1 210px", minWidth: 0 }}>
                    <TextField
                      required
                      fullWidth
                      size="small"
                      label="Name"
                      sx={compactFieldSx}
                      value={customerDetail.name}
                      onChange={(e) =>
                        handleChange("name", e.target.value.toUpperCase())
                      }
                      error={fieldErrors.name}
                      helperText={fieldErrors.name ? "Name is required" : ""}
                    />
                  </Box>

                  <Box sx={{ flex: "1 1 145px", minWidth: 0 }}>
                    <TextField
                      required
                      fullWidth
                      size="small"
                      label="Mobile"
                      placeholder="Enter number"
                      sx={compactFieldSx}
                      {...mobileFieldProps()}
                      value={customerDetail.mobile}
                      onChange={(e) => {
                        const mobile = sanitizeMobileInput(e.target.value);
                        handleChange("mobile", mobile);
                        checkMobileDuplicate(mobile);
                      }}
                      error={!!duplicateMobile || fieldErrors.mobile}
                      helperText={
                        duplicateMobile
                          ? `⚠️ Belongs to: ${duplicateMobile.name}`
                          : fieldErrors.mobile
                          ? "Mobile is required"
                          : ""
                      }
                      InputProps={{
                        endAdornment: isMobileChecking ? (
                          <InputAdornment position="end">
                            <CircularProgress size={14} />
                          </InputAdornment>
                        ) : null,
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: "1 1 155px", minWidth: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Date of Birth"
                      type="date"
                      sx={compactFieldSx}
                      InputLabelProps={{ shrink: true }}
                      value={customerDetail.dob}
                      onChange={(e) => handleChange("dob", e.target.value)}
                      helperText={(() => {
                        if (!customerDetail.dob) return "";
                        const birth = new Date(customerDetail.dob);
                        const today = new Date();
                        let age = today.getFullYear() - birth.getFullYear();
                        const m = today.getMonth() - birth.getMonth();
                        if (
                          m < 0 ||
                          (m === 0 && today.getDate() < birth.getDate())
                        )
                          age--;
                        return age >= 0 && age < 150 ? `Age: ${age} yr` : "";
                      })()}
                    />
                  </Box>

                  <Box sx={{ flex: "1 1 110px", minWidth: 0 }}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Relation"
                      sx={compactFieldSx}
                      value={customerDetail.relationType}
                      onChange={(e) =>
                        handleChange("relationType", e.target.value)
                      }
                    >
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="S/o">S/o</MenuItem>
                      <MenuItem value="D/o">D/o</MenuItem>
                      <MenuItem value="W/o">W/o</MenuItem>
                    </TextField>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
                  <Box sx={{ flex: "1 1 180px", minWidth: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Relative Name"
                      placeholder="Enter full name"
                      sx={compactFieldSx}
                      value={customerDetail.relationName}
                      onChange={(e) =>
                        handleChange(
                          "relationName",
                          e.target.value.toUpperCase(),
                        )
                      }
                    />
                  </Box>

                  <Box sx={{ flex: "1 1 150px", minWidth: 0 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Annual Income"
                      placeholder="e.g. 250000"
                      sx={compactFieldSx}
                      value={customerDetail.annualIncome}
                      onChange={(e) => handleAnnualIncomeChange(e.target.value)}
                      inputProps={{ inputMode: "decimal", min: 0 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: "2 1 220px", minWidth: 0 }}>
                    <TextField
                      required
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      label="Address"
                      sx={compactFieldSx}
                      value={customerDetail.pickupAddress}
                      onChange={(e) =>
                        handleChange("pickupAddress", e.target.value)
                      }
                      error={fieldErrors.pickupAddress}
                      helperText={
                        fieldErrors.pickupAddress ? "Address is required" : ""
                      }
                    />
                  </Box>

                  <Box sx={{ flex: "2 1 200px", minWidth: 0 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      label="Note"
                      sx={compactFieldSx}
                      value={customerDetail.note}
                      onChange={(e) => handleChange("note", e.target.value)}
                    />
                  </Box>
                </Box>
              </Stack>

              <Divider sx={{ my: 1.25 }} />

              {/* ── Documents: every slot on one row ─────────────────────── */}
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography
                  fontWeight={700}
                  sx={{
                    color: PRIMARY,
                    fontSize: { xs: "0.85rem", md: "0.9rem" },
                  }}
                >
                  Documents
                  <Box
                    component="span"
                    sx={{ fontWeight: 400, color: "#C5C5C5", fontSize: 11 }}
                  >
                    {" "}
                    *required
                  </Box>
                </Typography>
                <Box sx={{ flex: 1 }} />
                {hasOtherDocument ? (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={
                      <RemoveCircleOutlineIcon sx={{ fontSize: 15 }} />
                    }
                    onClick={handleRemoveOtherDocument}
                    sx={{
                      textTransform: "none",
                      borderRadius: "10px",
                      fontSize: "0.7rem",
                      py: 0.25,
                      minHeight: 28,
                    }}
                  >
                    Remove Other
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PersonAddRoundedIcon sx={{ fontSize: 15 }} />}
                    onClick={handleAddOtherDocument}
                    sx={{
                      textTransform: "none",
                      borderRadius: "10px",
                      fontSize: "0.7rem",
                      py: 0.25,
                      minHeight: 28,
                    }}
                  >
                    Add Other
                  </Button>
                )}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                  gap: 1,
                }}
              >
                {customerDetail.documents.map((doc, i) => {
                  const hasFile = doc.file && doc.file !== "No file";
                  return (
                    <Box
                      key={i}
                      sx={{
                        // Two-up on a phone, then all of them side by side.
                        flex: { xs: "1 1 calc(50% - 8px)", sm: "1 1 0" },
                        minWidth: 0,
                      }}
                    >
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 0.85,
                          borderRadius: 2,
                          height: "100%",
                          border: hasFile
                            ? `1px solid ${PRIMARY}40`
                            : "1px solid #e0e0e0",
                          backgroundColor: hasFile ? `${PRIMARY}04` : "#fff",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mb: 0.75,
                          }}
                        >
                          {hasFile ? (
                            <CheckCircleIcon
                              sx={{ fontSize: 15, color: "#4caf50" }}
                            />
                          ) : (
                            <ImageIcon sx={{ fontSize: 15, color: PRIMARY }} />
                          )}
                          <Typography
                            fontWeight={700}
                            sx={{
                              color: PRIMARY,
                              fontSize: "0.72rem",
                              flex: 1,
                              minWidth: 0,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {doc.name}
                          </Typography>
                          {doc.name === "Other" && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                const filtered =
                                  customerDetail.documents.filter(
                                    (_, idx) => idx !== i,
                                  );
                                handleChange("documents", filtered);
                              }}
                              sx={{ p: 0.25 }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          )}
                          <Tooltip title="Replace image">
                            <IconButton
                              size="small"
                              onClick={() =>
                                setImageSourceDialog({ open: true, index: i })
                              }
                              sx={{
                                p: 0.25,
                                color: PRIMARY,
                                backgroundColor: `${PRIMARY}10`,
                                "&:hover": { backgroundColor: `${PRIMARY}20` },
                              }}
                            >
                              <RefreshIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>

                        <TextField
                          size="small"
                          fullWidth
                          label="Number / ID"
                          value={doc.number}
                          onChange={(e) =>
                            updateDocument(i, "number", e.target.value)
                          }
                          placeholder={
                            doc.name === "Photo"
                              ? "From mobile no."
                              : `${doc.name} number`
                          }
                          disabled={doc.name === "Photo"}
                          sx={{
                            ...compactFieldSx,
                            mb: 0.75,
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              backgroundColor:
                                doc.name === "Photo" ? "#f5f5f5" : "#fff",
                            },
                          }}
                        />

                        <Box
                          sx={{
                            position: "relative",
                            width: "100%",
                            aspectRatio: "4 / 3",
                            minHeight: { xs: 130, sm: 150, md: 170 },
                            maxHeight: 240,
                            borderRadius: 2,
                            border: `2px dashed ${
                              hasFile ? PRIMARY : "#d0d0d0"
                            }`,
                            backgroundColor: hasFile
                              ? `${PRIMARY}08`
                              : "#fafafa",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            overflow: "hidden",
                            "&:hover": {
                              borderColor: PRIMARY,
                              backgroundColor: `${PRIMARY}04`,
                            },
                          }}
                          onClick={() => {
                            if (hasFile) {
                              let viewDoc = doc;
                              if (
                                typeof doc.file === "string" &&
                                doc.file.startsWith("data:") &&
                                (!doc.fileName || !doc.fileName.includes("."))
                              ) {
                                const mimeMatch = doc.file.match(
                                  /^data:([a-zA-Z]+\/([a-zA-Z0-9+\-_.]+));base64,/,
                                );
                                const ext = mimeMatch ? mimeMatch[2] : "jpg";
                                viewDoc = {
                                  ...doc,
                                  fileName: `${doc.name || "document"}.${ext}`,
                                };
                              }
                              setViewerDoc(viewDoc);
                            } else {
                              setImageSourceDialog({ open: true, index: i });
                            }
                          }}
                        >
                          {hasFile ? (
                            <Box
                              component="img"
                              src={doc.file}
                              alt={`${doc.name} preview`}
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <>
                              <CloudUploadIcon
                                sx={{
                                  fontSize: 24,
                                  color: PRIMARY,
                                  opacity: 0.8,
                                }}
                              />
                              <Typography
                                sx={{
                                  color: "text.secondary",
                                  textAlign: "center",
                                  fontSize: "0.65rem",
                                  mt: 0.25,
                                }}
                              >
                                Tap to upload
                              </Typography>
                            </>
                          )}
                        </Box>

                        <input
                          id={`camera-input-${i}`}
                          type="file"
                          hidden
                          accept="image/*"
                          capture="environment"
                          onChange={(e) => handleFileUpload(i, e)}
                        />
                        <input
                          id={`gallery-input-${i}`}
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => handleFileUpload(i, e)}
                        />
                      </Paper>
                    </Box>
                  );
                })}
              </Box>
            </Paper>

            {/* ══ NOMINEES SECTION ══ */}
            <NomineesSection
              fill
              mode={isNewCustomer ? "create" : "edit"}
              customerId={selectedCustomerId}
              customerName={customerDetail.name}
              canAddNominee={
                isNewCustomer
                  ? !!(
                      customerDetail.name.trim() && customerDetail.mobile.trim()
                    )
                  : !!selectedCustomerId
              }
              storeId={userStoreID}
              localNominees={localNominees}
              onLocalNomineesChange={setLocalNominees}
              onNomineeUpdated={updateNomineeInState}
              serverNominees={serverNominees}
              onRefresh={refreshNominees}
              showSnack={showSnack}
            />
          </Stack>
        </Box>
      </Box>

      {/* ─── Dialogs ──────────────────────────────────────────────────── */}
      <ImageSourceDialog
        open={imageSourceDialog.open}
        index={imageSourceDialog.index}
        prefix=""
        onClose={() => setImageSourceDialog({ open: false, index: null })}
        onTakePhoto={() => setCustomerCameraIndex(imageSourceDialog.index)}
      />
      <CameraCaptureDialog
        open={customerCameraIndex !== null}
        onClose={() => setCustomerCameraIndex(null)}
        onCapture={(dataUrl) => {
          if (customerCameraIndex !== null)
            setCustomerDocFile(
              customerCameraIndex,
              dataUrl,
              "camera-photo.jpg",
            );
        }}
      />

      <OTPDialog
        open={openOTPDialog}
        onClose={() => {
          setOpenOTPDialog(false);
          setPendingCustomerData(null);
        }}
        mobile={customerDetail.mobile}
        storeId={userStoreID}
        onVerifySuccess={() => saveCustomerAfterOTP()}
      />

      {/* Shared mobile number — ask, do not refuse. Whoever already holds the
          number is named, with the documents that tell the two apart. */}
      <Dialog
        open={!!duplicateConfirm}
        onClose={() => setDuplicateConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon sx={{ color: "#B45309" }} />
          <Typography fontWeight={700}>
            This number is already in use
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            <b>{customerDetail.mobile}</b> already belongs to{" "}
            <b>{duplicateMobile?.name}</b>.
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            If this really is a different person — a family sharing one phone —
            go ahead. Give them an Aadhar or PAN number so staff can tell the
            two apart when searching.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDuplicateConfirm(null)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              const payload = duplicateConfirm;
              setDuplicateConfirm(null);
              await proceedWithCreate(payload);
            }}
            sx={{ textTransform: "none", fontWeight: 600, bgcolor: PRIMARY }}
          >
            Different person — create
          </Button>
        </DialogActions>
      </Dialog>

      <BulkCustomerDialog
        open={openBulkDialog}
        onClose={() => setOpenBulkDialog(false)}
        storeId={userStoreID}
        createdBy={userID}
        allowDuplicateMobile={allowDuplicateMobile}
        onImported={() => {
          setPage(1);
          setCustomers([]);
          setHasMore(true);
          customersFetcher(1, false, true);
        }}
      />

      <DocumentViewerDialog
        doc={viewerDoc}
        onClose={() => setViewerDoc(null)}
      />

      {/* maxWidth/fullWidth so a long block-reason message can't stretch the
          paper past a phone's width (the other pawn confirms all do this). */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        {isCheckingLoans ? (
          <>
            <DialogTitle>Please wait</DialogTitle>
            <DialogContent
              sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 3 }}
            >
              <CircularProgress size={22} />
              <span>Checking for active loans…</span>
            </DialogContent>
          </>
        ) : deleteBlockInfo ? (
          <>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <WarningAmberIcon color="warning" />
              Cannot Delete Customer
            </DialogTitle>
            <DialogContent>{deleteBlockInfo.message}</DialogContent>
            <DialogActions>
              <Button
                variant="contained"
                onClick={() => setOpenDialog(false)}
                autoFocus
              >
                Understood
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogContent>
              Are you sure you want to delete customer "{selectedCustomer?.name}
              "?
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
              <Button
                color="error"
                variant="contained"
                onClick={() => deleteCustomer(selectedCustomer?.customer_id)}
              >
                Delete
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={snackBarConfig.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ "&.MuiSnackbar-root": { bottom: "56px !important" } }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackBarConfig.severity}
          variant="filled"
        >
          {snackBarConfig.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PawnCustomerMaster;
